import { useCfIdentity, useLogout } from "@civala/shared-ui"
import {
  ActionIcon,
  Avatar,
  Badge,
  Group,
  Indicator,
  Menu,
  Select,
  Text,
  Tooltip,
  UnstyledButton,
  useComputedColorScheme,
  useMantineColorScheme,
} from "@mantine/core"
import { IconBell, IconFlask, IconLogout, IconMoon, IconSettings, IconSun } from "@tabler/icons-react"
import { useNavigate } from "@tanstack/react-router"

import { COMPANIES } from "@/data/companies"
import { useApprovals } from "@/hooks/useApprovals"
import { useDemoMode } from "@/hooks/useDemoMode"
import { useUpdatePreferences } from "@/hooks/useMe"

/** Left-side topbar content for nexus: syndicate badge + company selector. */
export function NexusTopBarLeft() {
  const companyOptions = [
    { value: "portfolio", label: "Portfolio View" },
    ...COMPANIES.map((c) => ({ value: c.id, label: c.name })),
  ]

  return (
    <>
      <Badge variant="light" size="sm">
        Syndicate I
      </Badge>
      <Select data={companyOptions} defaultValue="portfolio" size="xs" w={180} />
    </>
  )
}

/** Right-side topbar content for nexus: theme toggle, approvals bell, avatar, sign-out. */
export function NexusTopBarRight() {
  const navigate = useNavigate()
  const { setColorScheme } = useMantineColorScheme()
  const computedColorScheme = useComputedColorScheme("light")
  const isDark = computedColorScheme === "dark"
  const identity = useCfIdentity()
  const logout = useLogout()
  // Real, cross-source pending-approval count feeds the bell (#110) — same query
  // the Approval Command Center uses (TanStack dedups, so no extra fetch).
  const { count } = useApprovals()
  // Persist the theme toggle to the user's account so it follows them across devices.
  const { updatePreferences } = useUpdatePreferences()
  const toggleColorScheme = () => {
    const next = isDark ? "light" : "dark"
    setColorScheme(next)
    void updatePreferences({ colorScheme: next })
  }
  // Device-local demo mode: off hides not-yet-live screens from the sidebar for a clean view.
  const [demoMode, setDemoMode] = useDemoMode()

  return (
    <>
      <Tooltip label={demoMode ? "Demo mode: showing in-progress screens" : "Demo mode: live screens only"}>
        <ActionIcon
          variant={demoMode ? "light" : "subtle"}
          color={demoMode ? "pitchNegative" : "gray"}
          size="md"
          onClick={() => setDemoMode((v) => !v)}
          aria-label="Toggle demo mode"
        >
          <IconFlask size={18} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={isDark ? "Light mode" : "Dark mode"}>
        <ActionIcon variant="subtle" size="md" onClick={toggleColorScheme}>
          {isDark ? <IconSun size={18} stroke={1.5} /> : <IconMoon size={18} stroke={1.5} />}
        </ActionIcon>
      </Tooltip>
      <Tooltip label={count > 0 ? `${count} awaiting approval` : "Approvals"}>
        <Indicator size={16} color="pitchNegative" label={count > 99 ? "99+" : count} disabled={count === 0} offset={4}>
          <ActionIcon variant="subtle" size="md" onClick={() => void navigate({ to: "/operations/approvals" })}>
            <IconBell size={18} stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Tooltip>
      <Menu position="bottom-end" width={200} withArrow>
        <Menu.Target>
          <UnstyledButton aria-label="Account menu">
            <Group gap="xs" style={{ cursor: "pointer" }}>
              <Avatar size="sm" radius="xl" color="pitchNavy">
                {identity.initials}
              </Avatar>
              <Text size="xs">{identity.givenName || identity.name}</Text>
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{identity.name || identity.email || "Account"}</Menu.Label>
          <Menu.Item leftSection={<IconSettings size={15} />} onClick={() => void navigate({ to: "/settings" })}>
            Settings
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item leftSection={<IconLogout size={15} />} onClick={logout}>
            Sign out
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </>
  )
}
